import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import V2PageLayout from '../../components/v2/V2PageLayout';
import { quenTenDaBiet } from '../../components/AccountMenu';
import userApi from '../../services/user';
import { ROLE } from '../../constants/roles.constant';

/**
 * Trang hồ sơ tài khoản.
 *
 * ── Vì sao trang này tồn tại ──
 * JWT chỉ mang `sub`, `role[]`, `zalo`, `store`, `center`, `source` — KHÔNG có
 * tên người dùng, và `center` chỉ là một ObjectId. Nên mọi thứ hiện ở đây đều
 * phải hỏi `GET /user/`. Ba route cần dùng đã có sẵn ở backend, không phải sửa
 * gì phía server:
 *   GET   /user/            → chính mình
 *   PATCH /user/            → đổi tên (bắt nhập mật khẩu hiện tại)
 *   PATCH /user/password    → đổi mật khẩu
 *
 * ── Cố ý không có gì ──
 * Không ảnh đại diện: hệ thống không lưu ảnh nào, dựng một vòng tròn chữ cái
 * đầu chỉ để lấp chỗ trống thì là trang trí. Không thẻ thống kê. Trang này chỉ
 * trả lời hai câu: tôi đang đăng nhập bằng ai, và tôi đổi mật khẩu ở đâu.
 */

// Tên vai trò để hiện cho người đọc, thay vì con số trong JWT.
const TEN_VAI = {
  [ROLE.ADMIN]: 'Quản trị hệ thống',
  [ROLE.DRIVING.ADMIN]: 'Quản trị đào tạo lái xe',
  [ROLE.DRIVING.TEACHER]: 'Giáo viên',
  [ROLE.DRIVING.REPORTER]: 'Báo cáo viên',
  [ROLE.DRIVING.DAT_ADMIN]: 'Quản trị DAT',
  [ROLE.DRIVING.DAT_TEACHER]: 'Giáo viên DAT',
};

const tenVai = (role) => {
  const ds = (Array.isArray(role) ? role : [role])
    .map((r) => TEN_VAI[Number(r)])
    .filter(Boolean);
  // Vai lạ vẫn phải hiện ra được — im lặng bỏ qua thì người dùng tưởng mình
  // không có quyền gì.
  return ds.length ? ds.join(', ') : 'Không xác định';
};

export default function ProfilePageV2() {
  const [dangTai, setDangTai] = useState(true);
  const [nguoiDung, setNguoiDung] = useState(null);

  const [ten, setTen] = useState('');
  const [matKhauDoiTen, setMatKhauDoiTen] = useState('');
  const [dangLuuTen, setDangLuuTen] = useState(false);

  const [matKhauCu, setMatKhauCu] = useState('');
  const [matKhauMoi, setMatKhauMoi] = useState('');
  const [matKhauNhacLai, setMatKhauNhacLai] = useState('');
  const [dangDoiMatKhau, setDangDoiMatKhau] = useState(false);

  useEffect(() => {
    userApi
      .getUser()
      .then((res) => {
        const u = res?.data ?? res;
        setNguoiDung(u);
        setTen(u?.name || '');
      })
      .catch((e) => toast.error(e?.response?.data?.message || 'Không tải được hồ sơ'))
      .finally(() => setDangTai(false));
  }, []);

  const luuTen = (e) => {
    e.preventDefault();
    if (!ten.trim()) return toast.warning('Tên không được để trống');
    setDangLuuTen(true);
    userApi
      .updateUser(ten.trim(), matKhauDoiTen)
      .then(() => {
        toast.success('Đã đổi tên');
        setMatKhauDoiTen('');
        quenTenDaBiet();   // để cụm tài khoản ở khung lấy lại tên mới
      })
      .catch((err) => toast.error(err?.response?.data?.message || 'Đổi tên không thành công'))
      .finally(() => setDangLuuTen(false));
  };

  const doiMatKhau = (e) => {
    e.preventDefault();
    // Chặn ngay ở giao diện: để backend từ chối thì người dùng mất một vòng
    // chờ mạng mới biết mình gõ lệch hai ô.
    if (matKhauMoi !== matKhauNhacLai) return toast.warning('Hai ô mật khẩu mới không giống nhau');
    if (!matKhauMoi) return toast.warning('Chưa nhập mật khẩu mới');
    setDangDoiMatKhau(true);
    userApi
      .changePassword({ password: matKhauCu, newPassword: matKhauMoi })
      .then(() => {
        toast.success('Đã đổi mật khẩu');
        setMatKhauCu(''); setMatKhauMoi(''); setMatKhauNhacLai('');
      })
      .catch((err) => toast.error(err?.response?.data?.message || 'Đổi mật khẩu không thành công'))
      .finally(() => setDangDoiMatKhau(false));
  };

  return (
    <V2PageLayout title='Hồ sơ tài khoản' gridClassName=''>
      <div style={{ maxWidth: 720, overflowY: 'auto', height: '100%' }}>
        {dangTai ? (
          <div className='py-4'><Spinner animation='border' size='sm' /> Đang tải…</div>
        ) : (
          <>
            <Card className='mb-3'>
              <Card.Body>
                <dl className='row mb-0'>
                  <dt className='col-sm-4 fw-normal text-muted'>Tên đăng nhập</dt>
                  <dd className='col-sm-8'>{nguoiDung?.userName || '—'}</dd>
                  <dt className='col-sm-4 fw-normal text-muted'>Vai trò</dt>
                  <dd className='col-sm-8'>{tenVai(nguoiDung?.role)}</dd>
                  <dt className='col-sm-4 fw-normal text-muted'>Email</dt>
                  <dd className='col-sm-8'>{nguoiDung?.email || '—'}</dd>
                  <dt className='col-sm-4 fw-normal text-muted'>Điện thoại</dt>
                  <dd className='col-sm-8 mb-0'>{nguoiDung?.phone || nguoiDung?.zalo || '—'}</dd>
                </dl>
              </Card.Body>
            </Card>

            <Card className='mb-3'>
              <Card.Body>
                <Card.Title as='h2' className='h6'>Tên hiển thị</Card.Title>
                <Form onSubmit={luuTen}>
                  <Row className='g-2 align-items-end'>
                    <Col md={5}>
                      <Form.Label htmlFor='ho-so-ten'>Tên</Form.Label>
                      <Form.Control
                        id='ho-so-ten' value={ten}
                        onChange={(e) => setTen(e.target.value)}
                      />
                    </Col>
                    <Col md={5}>
                      <Form.Label htmlFor='ho-so-mk-xac-nhan'>Mật khẩu hiện tại</Form.Label>
                      <Form.Control
                        id='ho-so-mk-xac-nhan' type='password' autoComplete='current-password'
                        value={matKhauDoiTen} onChange={(e) => setMatKhauDoiTen(e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Button type='submit' disabled={dangLuuTen} className='w-100'>
                        {dangLuuTen ? 'Đang lưu…' : 'Lưu tên'}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            <Card className='mb-4'>
              <Card.Body>
                <Card.Title as='h2' className='h6'>Đổi mật khẩu</Card.Title>
                <Form onSubmit={doiMatKhau}>
                  <Row className='g-2'>
                    <Col md={4}>
                      <Form.Label htmlFor='mk-cu'>Mật khẩu hiện tại</Form.Label>
                      <Form.Control
                        id='mk-cu' type='password' autoComplete='current-password'
                        value={matKhauCu} onChange={(e) => setMatKhauCu(e.target.value)}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Label htmlFor='mk-moi'>Mật khẩu mới</Form.Label>
                      <Form.Control
                        id='mk-moi' type='password' autoComplete='new-password'
                        value={matKhauMoi} onChange={(e) => setMatKhauMoi(e.target.value)}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Label htmlFor='mk-nhac-lai'>Nhập lại mật khẩu mới</Form.Label>
                      <Form.Control
                        id='mk-nhac-lai' type='password' autoComplete='new-password'
                        value={matKhauNhacLai} onChange={(e) => setMatKhauNhacLai(e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Button type='submit' disabled={dangDoiMatKhau} className='mt-3'>
                    {dangDoiMatKhau ? 'Đang đổi…' : 'Đổi mật khẩu'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </V2PageLayout>
  );
}
